// ScoringConfigurationService.ts - Service for managing scoring configurations

import { 
  ScoringConfiguration, 
  ScoringPreset, 
  ScoringPresetConfig,
  ScoringValidationResult,
  ScoringConfigExport,
  BaseScoreConfig,
  ScoringWeights,
  ResponseTimeBonusConfig,
  GradeThreshold,
  UIThresholds,
  RecommendationTriggers
} from '../types/ScoringConfiguration';
import { logger } from './LoggingService';

// Storage key
const SCORING_CONFIG_KEY = 'cybersec-risk-scoring-config';
const ACTIVE_CONFIG_KEY = 'cybersec-risk-active-scoring';

export class ScoringConfigurationService {
  // Migrate old configurations to include new fields
  private static migrateConfiguration(config: ScoringConfiguration): ScoringConfiguration {
    let migrated = { ...config };
    let needsMigration = false;
    
    // Migrate old grade thresholds to new rating system
    const hasOldGrades = config.gradeThresholds.some(t => 
      t.grade.match(/^[A-F][+-]?$/) || t.grade === 'F'
    );
    
    if (hasOldGrades) {
      migrated.gradeThresholds = [
        { minScore: 85, grade: 'EXCELLENT', description: 'Outstanding response that strengthens your security posture significantly.', label: 'Excellent' },
        { minScore: 70, grade: 'WISE', description: 'Smart decision that improves your overall security and business resilience.', label: 'Wise' },
        { minScore: 50, grade: 'DECENT', description: 'Reasonable response with balanced trade-offs, but room for improvement.', label: 'Decent' },
        { minScore: 30, grade: 'OK', description: 'Acceptable response but with some negative impacts that could have been avoided.', label: 'OK' },
        { minScore: 0, grade: 'POOR', description: 'Suboptimal response that significantly weakens your security position.', label: 'Poor' }
      ];
      needsMigration = true;
    }
    
    // Add missing UI thresholds
    if (!config.uiThresholds) {
      migrated.uiThresholds = {
        mitigationCap: {
          low: 0.2,
          medium: 0.5,
          high: 0.8
        },
        performanceRatings: {
          excellent: 85,
          good: 70,
          average: 50,
          poor: 30
        },
        impactSignificance: {
          critical: 25,
          major: 15,
          moderate: 5,
          minor: 1
        },
        colorCoding: {
          positive: 10,
          neutral: -5,
          negative: -10
        }
      };
      needsMigration = true;
    }
    
    // Add missing recommendation triggers
    if (!config.recommendationTriggers) {
      migrated.recommendationTriggers = {
        lowSecurityScore: 40,
        lowOperationalScore: 40,
        lowReputationScore: 40,
        lowFinancialScore: 40,
        lackOfInvestments: 3,
        unbalancedPortfolio: 0.3,
        consistentPoorPerformance: 3,
        responseTimeIssues: 0.8
      };
      needsMigration = true;
    }
    
    if (needsMigration) {
      migrated.version = (config.version || 0) + 1;
      migrated.updatedAt = new Date().toISOString();
    }
    
    return migrated;
  }

  // Get all scoring configurations
  static async getAll(): Promise<ScoringConfiguration[]> {
    try {
      const stored = localStorage.getItem(SCORING_CONFIG_KEY);
      if (!stored) {
        // Initialize with default configuration without calling save() to avoid circular dependency
        const defaultConfig = this.getDefaultConfiguration();
        // Directly set in localStorage
        localStorage.setItem(SCORING_CONFIG_KEY, JSON.stringify([defaultConfig]));
        return [defaultConfig];
      }
      
      const configs = JSON.parse(stored);
      const configArray = Array.isArray(configs) ? configs : [configs];
      
      // Migrate any old configurations
      const migratedConfigs = configArray.map((config: ScoringConfiguration) => 
        this.migrateConfiguration(config)
      );
      
      // Save migrated configs back to localStorage if any were migrated
      const needsSave = configArray.some((config: ScoringConfiguration) => 
        config.gradeThresholds.some(t => t.grade.match(/^[A-F][+-]?$/) || t.grade === 'F') ||
        !config.uiThresholds ||
        !config.recommendationTriggers
      );
      
      if (needsSave) {
        localStorage.setItem(SCORING_CONFIG_KEY, JSON.stringify(migratedConfigs));
      }
      
      return migratedConfigs;
    } catch (error) {
      logger.error('Error loading scoring configurations', 'ScoringConfigurationService', {}, error as Error);
      return [this.getDefaultConfiguration()];
    }
  }

  // Get active configuration
  static async getActive(): Promise<ScoringConfiguration> {
    try {
      const activeId = localStorage.getItem(ACTIVE_CONFIG_KEY);
      if (!activeId) {
        return this.getDefaultConfiguration();
      }
      
      const configs = await this.getAll();
      const active = configs.find(c => c.id === activeId);
      return active || this.getDefaultConfiguration();
    } catch (error) {
      logger.error('Error loading active configuration', 'ScoringConfigurationService', {}, error as Error);
      return this.getDefaultConfiguration();
    }
  }

  // Set active configuration
  static async setActive(configId: string): Promise<void> {
    localStorage.setItem(ACTIVE_CONFIG_KEY, configId);
  }

  // Save configuration
  static async save(config: ScoringConfiguration): Promise<ScoringConfiguration> {
    const validation = this.validate(config);
    if (!validation.isValid) {
      throw new Error(`Invalid configuration: ${validation.errors.join(', ')}`);
    }

    const configs = await this.getAll();
    const index = configs.findIndex(c => c.id === config.id);
    
    // Apply migration to ensure new format
    const migratedConfig = this.migrateConfiguration(config);
    
    const updatedConfig = {
      ...migratedConfig,
      updatedAt: new Date().toISOString(),
      version: (config.version || 0) + 1
    };

    if (index >= 0) {
      configs[index] = updatedConfig;
    } else {
      configs.push(updatedConfig);
    }

    localStorage.setItem(SCORING_CONFIG_KEY, JSON.stringify(configs));
    return updatedConfig;
  }

  // Delete configuration
  static async delete(configId: string): Promise<void> {
    if (configId === 'default') {
      throw new Error('Cannot delete default configuration');
    }

    const configs = await this.getAll();
    const filtered = configs.filter(c => c.id !== configId);
    localStorage.setItem(SCORING_CONFIG_KEY, JSON.stringify(filtered));

    // If deleted config was active, switch to default
    const activeId = localStorage.getItem(ACTIVE_CONFIG_KEY);
    if (activeId === configId) {
      await this.setActive('default');
    }
  }

  // Apply preset
  static async applyPreset(preset: ScoringPreset): Promise<ScoringConfiguration> {
    const presetConfig = this.getPresetConfiguration(preset);
    const baseConfig = this.getDefaultConfiguration();
    
    const mergedConfig: ScoringConfiguration = {
      ...baseConfig,
      ...presetConfig.config,
      id: `preset-${preset}-${Date.now()}`,
      name: `${presetConfig.name} Configuration`,
      description: presetConfig.description,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1
    };

    return await this.save(mergedConfig);
  }

  // Export configuration
  static async export(configId: string): Promise<ScoringConfigExport> {
    const configs = await this.getAll();
    const config = configs.find(c => c.id === configId);
    
    if (!config) {
      throw new Error('Configuration not found');
    }

    return {
      version: '1.0',
      exportDate: new Date().toISOString(),
      configuration: config
    };
  }

  // Import configuration
  static async import(data: ScoringConfigExport): Promise<ScoringConfiguration> {
    if (!data || !data.configuration) {
      throw new Error('Invalid import data');
    }

    const config = {
      ...data.configuration,
      id: `imported-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1
    };

    return await this.save(config);
  }

  // Validate configuration
  static validate(config: ScoringConfiguration): ScoringValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields
    if (!config.name) errors.push('Name is required');
    if (!config.baseScores) errors.push('Base scores are required');
    if (!config.categoryWeights) errors.push('Category weights are required');
    if (!config.investmentEffects) errors.push('Investment effects are required');
    if (!config.gradeThresholds || config.gradeThresholds.length === 0) {
      errors.push('Grade thresholds are required');
    }
    if (!config.uiThresholds) errors.push('UI thresholds are required');
    if (!config.recommendationTriggers) errors.push('Recommendation triggers are required');

    // Validate weights sum to 1.0
    if (config.categoryWeights) {
      const sum = Object.values(config.categoryWeights).reduce((a, b) => a + b, 0);
      if (Math.abs(sum - 1.0) > 0.01) {
        errors.push(`Category weights must sum to 1.0 (current: ${sum})`);
      }
    }

    // Validate base scores
    if (config.baseScores) {
      Object.entries(config.baseScores).forEach(([key, value]) => {
        if (value < 0 || value > 100) {
          errors.push(`Base score for ${key} must be between 0 and 100`);
        }
      });
    }

    // Validate grade thresholds
    if (config.gradeThresholds) {
      const sorted = [...config.gradeThresholds].sort((a, b) => b.minScore - a.minScore);
      for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i].minScore <= sorted[i + 1].minScore) {
          errors.push('Grade thresholds must have decreasing minimum scores');
          break;
        }
      }
    }

    // Validate UI thresholds
    if (config.uiThresholds) {
      const ui = config.uiThresholds;
      if (ui.mitigationCap.low >= ui.mitigationCap.medium || ui.mitigationCap.medium >= ui.mitigationCap.high) {
        errors.push('Mitigation cap thresholds must be in ascending order');
      }
      if (ui.performanceRatings.poor >= ui.performanceRatings.average || 
          ui.performanceRatings.average >= ui.performanceRatings.good ||
          ui.performanceRatings.good >= ui.performanceRatings.excellent) {
        errors.push('Performance rating thresholds must be in ascending order');
      }
    }
    
    // Validate recommendation triggers
    if (config.recommendationTriggers) {
      const triggers = config.recommendationTriggers;
      if (triggers.unbalancedPortfolio < 0 || triggers.unbalancedPortfolio > 1) {
        errors.push('Unbalanced portfolio threshold must be between 0 and 1');
      }
      if (triggers.responseTimeIssues < 0 || triggers.responseTimeIssues > 2) {
        errors.push('Response time issues threshold must be between 0 and 2');
      }
    }

    // Warnings
    if (config.difficultyMultiplier && (config.difficultyMultiplier < 0.5 || config.difficultyMultiplier > 2.0)) {
      warnings.push('Difficulty multiplier outside recommended range (0.5-2.0)');
    }

    if (config.maxIncidentMitigation && config.maxIncidentMitigation > 0.75) {
      warnings.push('High incident mitigation cap may make game too easy');
    }
    
    if (config.recommendationTriggers && config.recommendationTriggers.lackOfInvestments < 2) {
      warnings.push('Very low investment threshold may trigger too many recommendations');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  // Get default configuration
  static getDefaultConfiguration(): ScoringConfiguration {
    const baseScores: BaseScoreConfig = {
      security: 50,
      reputation: 50,
      financial: 50,
      operational: 50
    };

    const categoryWeights: ScoringWeights = {
      security: 0.3,
      reputation: 0.3,
      financial: 0.2,
      operational: 0.2
    };

    const responseTimeBonus: ResponseTimeBonusConfig = {
      excellentThreshold: 0.5,
      excellentBonus: 10,
      goodThreshold: 0.75,
      goodBonus: 5,
      acceptableThreshold: 1.0,
      acceptableBonus: 2
    };

    const gradeThresholds: GradeThreshold[] = [
      { minScore: 85, grade: 'EXCELLENT', description: 'Outstanding response that strengthens your security posture significantly.', label: 'Excellent' },
      { minScore: 70, grade: 'WISE', description: 'Smart decision that improves your overall security and business resilience.', label: 'Wise' },
      { minScore: 50, grade: 'DECENT', description: 'Reasonable response with balanced trade-offs, but room for improvement.', label: 'Decent' },
      { minScore: 30, grade: 'OK', description: 'Acceptable response but with some negative impacts that could have been avoided.', label: 'OK' },
      { minScore: 0, grade: 'POOR', description: 'Suboptimal response that significantly weakens your security position.', label: 'Poor' }
    ];

    // Import existing investment effects from calculator
    const investmentEffects = {
      'security-training': {
        categoryBonuses: { security: 15, reputation: 5, financial: 0, operational: 5 },
        incidentMitigation: 0.1,
        responseTimeImprovement: 0.05
      },
      'incident-response-team': {
        categoryBonuses: { security: 10, reputation: 10, financial: 0, operational: 15 },
        incidentMitigation: 0.15,
        responseTimeImprovement: 0.2
      },
      'security-monitoring': {
        categoryBonuses: { security: 20, reputation: 5, financial: 0, operational: 10 },
        incidentMitigation: 0.2,
        responseTimeImprovement: 0.15
      },
      'backup-recovery': {
        categoryBonuses: { security: 5, reputation: 5, financial: 10, operational: 20 },
        incidentMitigation: 0.25,
        responseTimeImprovement: 0.1
      },
      'cyber-insurance': {
        categoryBonuses: { security: 0, reputation: 10, financial: 25, operational: 5 },
        incidentMitigation: 0.0,
        responseTimeImprovement: 0.0
      },
      'compliance-program': {
        categoryBonuses: { security: 10, reputation: 15, financial: 5, operational: 10 },
        incidentMitigation: 0.05,
        responseTimeImprovement: 0.05
      },
      'vendor-management': {
        categoryBonuses: { security: 10, reputation: 10, financial: 5, operational: 10 },
        incidentMitigation: 0.1,
        responseTimeImprovement: 0.05
      },
      'penetration-testing': {
        categoryBonuses: { security: 25, reputation: 5, financial: 0, operational: 5 },
        incidentMitigation: 0.15,
        responseTimeImprovement: 0.1
      },
      'zero-trust-architecture': {
        categoryBonuses: { security: 30, reputation: 10, financial: 0, operational: 15 },
        incidentMitigation: 0.3,
        responseTimeImprovement: 0.1
      },
      'ai-threat-detection': {
        categoryBonuses: { security: 25, reputation: 5, financial: 0, operational: 20 },
        incidentMitigation: 0.25,
        responseTimeImprovement: 0.15
      },
      'data-encryption': {
        categoryBonuses: { security: 15, reputation: 10, financial: 5, operational: 5 },
        incidentMitigation: 0.1,
        responseTimeImprovement: 0.05
      }
    };

    const investmentSynergies = [
      {
        investmentId: 'security-training',
        incidentCategories: ['phishing', 'social-engineering'],
        bonusMultiplier: 2.0,
        description: 'Security training is particularly effective against human-factor attacks'
      },
      {
        investmentId: 'zero-trust-architecture',
        incidentCategories: ['data-breach', 'insider-threat', 'lateral-movement'],
        bonusMultiplier: 1.75,
        description: 'Zero trust significantly limits the impact of breaches and insider threats'
      },
      {
        investmentId: 'ai-threat-detection',
        incidentCategories: ['advanced-persistent-threat', 'zero-day', 'anomalous-behavior'],
        bonusMultiplier: 1.8,
        description: 'AI detection excels at identifying sophisticated and novel threats'
      },
      {
        investmentId: 'incident-response-team',
        incidentCategories: ['ransomware', 'data-breach', 'system-compromise'],
        bonusMultiplier: 1.5,
        unlocksBetterOptions: true,
        description: 'Dedicated response team enables faster and more effective incident handling'
      },
      {
        investmentId: 'backup-recovery',
        incidentCategories: ['ransomware', 'data-loss', 'system-failure'],
        bonusMultiplier: 2.5,
        description: 'Backup systems are critical for ransomware and data loss scenarios'
      },
      {
        investmentId: 'security-monitoring',
        incidentCategories: ['intrusion', 'data-exfiltration', 'suspicious-activity'],
        bonusMultiplier: 1.6,
        description: 'Continuous monitoring enables early detection and response'
      },
      {
        investmentId: 'vendor-management',
        incidentCategories: ['supply-chain', 'third-party-breach', 'vendor-compromise'],
        bonusMultiplier: 2.0,
        description: 'Vendor management is essential for supply chain security'
      },
      {
        investmentId: 'data-encryption',
        incidentCategories: ['data-breach', 'data-theft', 'unauthorized-access'],
        bonusMultiplier: 1.7,
        description: 'Encryption significantly reduces the impact of data breaches'
      }
    ];

    // UI Configuration
    const uiThresholds: UIThresholds = {
      mitigationCap: {
        low: 0.2,     // < 20% mitigation
        medium: 0.5,  // 20-50% mitigation
        high: 0.8     // > 50% mitigation
      },
      performanceRatings: {
        excellent: 85,
        good: 70,
        average: 50,
        poor: 30
      },
      impactSignificance: {
        critical: 25,
        major: 15,
        moderate: 5,
        minor: 1
      },
      colorCoding: {
        positive: 10,
        neutral: -5,
        negative: -5
      }
    };

    // Recommendation triggers
    const recommendationTriggers: RecommendationTriggers = {
      lowSecurityScore: 50,
      lowOperationalScore: 50,
      lowReputationScore: 50,
      lowFinancialScore: 50,
      lackOfInvestments: 3,
      unbalancedPortfolio: 0.3, // 30% difference
      consistentPoorPerformance: 3,
      responseTimeIssues: 0.8 // 80% of time limit
    };

    return {
      id: 'default',
      name: 'Default Scoring',
      description: 'Standard scoring configuration for balanced gameplay',
      isActive: true,
      baseScores,
      categoryWeights,
      investmentEffects,
      investmentSynergies,
      responseTimeBonus,
      maxResponseTimeImprovement: 0.4,
      maxIncidentMitigation: 0.5,
      gradeThresholds,
      uiThresholds,
      recommendationTriggers,
      enableSynergies: true,
      enableResponseTimeBonus: true,
      difficultyMultiplier: 1.0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1
    };
  }

  // Get preset configurations
  static getPresetConfiguration(preset: ScoringPreset): ScoringPresetConfig {
    const presets: Record<ScoringPreset, ScoringPresetConfig> = {
      default: {
        id: 'default',
        name: 'Default',
        description: 'Standard balanced scoring',
        config: {}
      },
      hardcore: {
        id: 'hardcore',
        name: 'Hardcore',
        description: 'Challenging mode with harsh penalties',
        config: {
          baseScores: {
            security: 40,
            reputation: 40,
            financial: 40,
            operational: 40
          },
          difficultyMultiplier: 1.5,
          maxIncidentMitigation: 0.3,
          maxResponseTimeImprovement: 0.2,
          responseTimeBonus: {
            excellentThreshold: 0.3,
            excellentBonus: 15,
            goodThreshold: 0.5,
            goodBonus: 8,
            acceptableThreshold: 0.75,
            acceptableBonus: 3
          }
        }
      },
      balanced: {
        id: 'balanced',
        name: 'Balanced',
        description: 'Equal weight across all categories',
        config: {
          categoryWeights: {
            security: 0.25,
            reputation: 0.25,
            financial: 0.25,
            operational: 0.25
          }
        }
      },
      forgiving: {
        id: 'forgiving',
        name: 'Forgiving',
        description: 'Easier mode with generous bonuses',
        config: {
          baseScores: {
            security: 60,
            reputation: 60,
            financial: 60,
            operational: 60
          },
          difficultyMultiplier: 0.75,
          maxIncidentMitigation: 0.7,
          maxResponseTimeImprovement: 0.6,
          responseTimeBonus: {
            excellentThreshold: 0.7,
            excellentBonus: 8,
            goodThreshold: 0.9,
            goodBonus: 4,
            acceptableThreshold: 1.2,
            acceptableBonus: 2
          }
        }
      },
      educational: {
        id: 'educational',
        name: 'Educational',
        description: 'Focus on learning with detailed feedback',
        config: {
          baseScores: {
            security: 55,
            reputation: 55,
            financial: 55,
            operational: 55
          },
          enableSynergies: true,
          enableResponseTimeBonus: true,
          categoryWeights: {
            security: 0.4,
            reputation: 0.2,
            financial: 0.2,
            operational: 0.2
          }
        }
      }
    };

    return presets[preset] || presets.default;
  }

  // Get all available presets
  static getAvailablePresets(): ScoringPresetConfig[] {
    return [
      this.getPresetConfiguration('default'),
      this.getPresetConfiguration('balanced'),
      this.getPresetConfiguration('forgiving'),
      this.getPresetConfiguration('hardcore'),
      this.getPresetConfiguration('educational')
    ];
  }
}